import React from 'react'
import styled, { css } from 'styled-components'
import { Block } from '../block/block'
import { VisuallyHidden } from '../visuallyHidden/visuallyHidden'
import { useCheckbox } from './checkbox.context'

const CheckMark = ({ isChecked, ...rest }) => (
  <svg
    width='10'
    height='8'
    viewBox='0 0 10 8'
    fill='none'
    xmlns='http://www.w3.org/2000/svg'
    aria-hidden={true}
    focusable={false}
    role='presentation'
    {...rest}
  >
    <path
      d='M1.57141 4.50797L3.47617 7.04766'
      stroke='white'
      strokeWidth={1}
      strokeLinecap='round'
      strokeLinejoin='round'
    />
    <path
      d='M9.19043 1.33336L3.47614 7.04765'
      stroke='white'
      strokeWidth={1}
      strokeLinecap='round'
      strokeLinejoin='round'
    />
  </svg>
)

export const Checkbox = ({
  checked,
  defaultChecked,
  onChange,
  value = '',
  label,
  labelPosition = 'right',
  disabled = false,
  invalid = false,
  ...rest
}) => {
  const { updateState, inGroup, disabledAll, values } = useCheckbox()
  const isDisabled = React.useMemo(
    () => disabledAll || disabled,
    [disabled, disabledAll]
  )
  const [isChecked, setIsChecked] = React.useState(!!defaultChecked)
  const hasLabel = React.useMemo(() => Boolean(label), [label])

  React.useEffect(() => {
    if (!inGroup) return

    const nextValue = values.includes(value)
    if (nextValue === isChecked) return
    setIsChecked(nextValue)
  }, [inGroup, values.join(',')])

  React.useEffect(() => {
    if (checked === undefined) return
    setIsChecked(checked)
  }, [checked])

  function handleOnChange(event) {
    if (isDisabled) return

    const customEvent = {
      target: {
        checked: !isChecked
      },
      stopPropagation: event.stopPropagation,
      preventDefault: event.preventDefault,
      nativeEvent: event
    }

    if (inGroup) updateState?.(value, !isChecked)
    setIsChecked(!isChecked)
    onChange?.(customEvent)
  }

  return (
    <Label disabled={isDisabled}>
      {hasLabel && labelPosition === 'left' && (
        <CheckboxText className='checkbox-text'>{label}</CheckboxText>
      )}
      <CheckboxContainer
        className='checkbox-container'
        checked={isChecked}
        disabled={isDisabled}
        tabIndex={isDisabled ? -1 : 0}
      >
        <VisuallyHidden
          as='input'
          type='checkbox'
          className='checkbox-input'
          tabIndex={-1}
          disabled={isDisabled}
          checked={isChecked}
          invalid={invalid}
          aria-checked={isChecked}
          aria-disabled={isDisabled}
          aria-invalid={invalid}
          onChange={handleOnChange}
          {...rest}
        />
        <StyledCheckMark isChecked={isChecked} />
      </CheckboxContainer>
      {hasLabel && labelPosition === 'right' && (
        <CheckboxText className='checkbox-text'>{label}</CheckboxText>
      )}
    </Label>
  )
}

const CheckboxContainer = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 24px;
  height: 24px;
  min-height: 24px;
  min-width: 24px;
  padding: 4px;
  border-radius: 4px;
  border: 1px solid;
  flex-shrink: 0;
  background-color: transparent;
  overflow: hidden;
  transition-property: border, background;
  transition-duration: 150ms;
  transition-timing-function: ease-in;

  ${({ disabled }) =>
    disabled
      ? css`
          opacity: 0.4;
          cursor: not-allowed;
        `
      : css`
          opacity: 1;
          cursor: pointer;
        `}

  ${({ checked }) =>
    checked
      ? css`
          border-color: #ac0ae8;
          background-color: #ac0ae8;

          svg {
            opacity: 1;
            visibility: visible;
            transform: scale(1);
          }

          &:hover {
            border-color: #9b09d1;
            background-color: #9b09d1;
          }

          &:active {
            border-color: #7807a2;
            background-color: #7807a2;
          }
        `
      : css`
          border-color: #d1d1e0;

          svg {
            opacity: 0;
            visibility: hidden;
            transform: scale(0.5);
          }

          &:hover {
            border-color: #ac0ae8;
          }
        `};

  ${({ checked, disabled }) =>
    checked &&
    disabled &&
    css`
      border-color: #25252d;
      background-color: #25252d;

      &:hover,
      &:active {
        border-color: #25252d;
        background-color: #25252d;
      }
    `};
`

const Label = styled.label`
  position: relative;
  display: inline-flex;
  justify-content: flex-start;
  align-items: center;
  vertical-align: top;
  width: auto;
  gap: 8px;
  transition: all 150ms ease;

  ${({ disabled }) =>
    disabled
      ? css`
          opacity: 0.75;
          cursor: not-allowed;

          span.checkbox-text {
            pointer-events: none;
          }
        `
      : css`
          opacity: 1;
        `};
`

const CheckboxText = styled.span`
  display: inline-block;
  font-size: 15px;
  line-height: 120%;
  font-style: normal;
  font-weight: 400;
  color: #25252d;
  user-select: none;
`

const StyledCheckMark = styled(CheckMark)`
  height: 100%;
  width: 100%;
  transition-duration: 150ms;
  transition-timing-function: ease-in;
  transition-property: transform, opacity, visibility;

  ${({ isChecked }) =>
    isChecked &&
    css`
      path {
        animation: move 100ms cubic-bezier(0.65, 0, 0.45, 1) 150ms forwards;
        stroke-dasharray: 4;
        stroke-dashoffset: 4;

        @keyframes move {
          100% {
            stroke-dashoffset: 0;
          }
        }
      }

      path:last-of-type {
        stroke-dasharray: 10;
        stroke-dashoffset: -10;
        animation-delay: 250ms;
      }
    `};
`
